/* eslint-disable */

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Edit3,
  FileText,
  MessageSquare,
  Paperclip,
  Repeat,
  StickyNote,
  Trash,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ensureRichText } from '../../lib/rich-text';
import {
  getDefaultTaskStatusTab,
  type Task,
  toggleTaskStatusKey,
  type WorkspaceMember,
} from '../../lib/task-list';
import { type RouterOutputs, trpc } from '../../lib/trpc';
import type { User as CommentUser } from '../../types/comments';
import {
  emptyTaskForm,
  fromDueAtSeconds,
  type TaskFormValues,
  taskFormSchema,
  toCreateTaskInput,
} from '../../types/task-form';
import { Button } from '../../ui/button';
import { TaskCommentThread } from '../comments/TaskCommentThread';
import { TaskFileAttachments } from '../comments/task-file-attachments';
import { EntityFeatureBar, type EntityFeatureBarItem } from '../features/EntityFeatureBar';
import { NotesPanel } from '../notes/NotesPanel';
import { RichTextContent } from '../rich-text-content';
import { TaskCard } from './TaskCard';
import { TaskFormFields } from './TaskFormFields';

/** A fully-loaded task, as the detail surfaces receive it. Wider than `Task` in `lib/task-list`,
 * which is the list row. */
export type TaskDetail = NonNullable<RouterOutputs['tasks']['getById']>;

export interface TaskSideloadContentProps {
  /** Receives the payload already shaped for a task write — see `toCreateTaskInput`. */
  onSubmit: (data: ReturnType<typeof toCreateTaskInput>) => void;
  task: TaskDetail | null;
  workspaceMembers: Array<{ id: number; email: string; fullName: string | null }>;
  isSubmitting?: boolean;
  onDelete?: () => void;
  isDeleting?: boolean;
  initialMode?: 'view' | 'edit';
  onClose: () => void;
  isReadOnly?: boolean;
}

export function TaskSideloadContent({
  onSubmit,
  task,
  workspaceMembers,
  isSubmitting = false,
  onDelete,
  isDeleting = false,
  initialMode = 'view',
  onClose,
  isReadOnly = false,
}: TaskSideloadContentProps) {
  const [mode, setMode] = useState<'view' | 'edit'>(isReadOnly ? 'view' : initialMode);
  const [activeTab, setActiveTab] = useState<'details' | 'comments' | 'files' | 'notes'>('details');

  const utils = trpc.useUtils();
  const { data: flags } = trpc.featureFlags.list.useQuery();

  const showComments = flags ? flags['task:comments'] !== false : true;
  const showFiles = flags ? (flags['task:files'] ?? flags.docs) !== false : true;
  const showNotes = flags ? flags['task:notes'] === true : false;

  const { data: files } = trpc.files.getForTarget.useQuery(
    { targetType: 'task', targetId: task?.id ?? 0 },
    { enabled: Boolean(task?.id) && showFiles }
  );
  const fileCount = files?.length ?? 0;

  const { data: thread } = trpc.threads.getThreadForTask.useQuery(
    { taskId: task?.id ?? 0 },
    { enabled: Boolean(task?.id) && showComments }
  );

  const { data: commentsData } = trpc.comments.getForThread.useQuery(
    { threadId: thread?.id ?? 0, limit: 50 },
    { enabled: Boolean(thread?.id) && showComments }
  );
  const commentCount = commentsData?.items?.filter(Boolean).length ?? 0;

  const toggleMutation = trpc.tasks.update.useMutation({
    onSuccess: () => {
      if (task?.id) utils.tasks.getById.invalidate({ id: task.id });
      utils.tasks.getAll.invalidate();
    },
  });

  const statusesQuery = trpc.taskStatuses.list.useQuery();
  const statuses = statusesQuery.data?.items ?? [];

  const defaultStatus = getDefaultTaskStatusTab(statuses);

  const methods = useForm<TaskFormValues>({
    resolver: zodResolver(taskFormSchema),
    mode: 'onChange',
    defaultValues: emptyTaskForm(defaultStatus),
  });

  useEffect(() => {
    setMode(isReadOnly ? 'view' : initialMode);
  }, [initialMode, isReadOnly]);

  useEffect(() => {
    if (!task) return;
    methods.reset({
      ...emptyTaskForm(defaultStatus),
      title: task.label || '',
      description: ensureRichText(task.content),
      status: task.status || defaultStatus,
      assignedTo: task.assignedTo ?? null,
      priority: task.priority || 0,
      contactId: task.contactId ?? null,
      ...fromDueAtSeconds(task.dueAt),
      reminderEnabled: !!task.remindAt,
      remindBeforeMinutes: task.remindBeforeMinutes ?? 15,
      repeatFrequency: (task.reminderFreq ?? 'never') as TaskFormValues['repeatFrequency'],
      endRepeatEnabled: !!task.endRepeatAt,
      endRepeatDate: task.endRepeatAt ? new Date(task.endRepeatAt * 1000) : null,
    });
    void methods.trigger();
  }, [task, methods, defaultStatus]);

  const handleSubmit = methods.handleSubmit((values) => {
    if (isReadOnly) return;
    onSubmit(toCreateTaskInput(values));
  });

  const handleToggleDone = (t: Task) => {
    if (!task) return;
    const nextStatus = toggleTaskStatusKey(statuses, t.status);
    toggleMutation.mutate({ id: task.id, status: nextStatus });
  };

  const getReportedByName = () => {
    if (!task?.reportedBy) return null;
    const member = workspaceMembers.find((m) => m.id === task.reportedBy);
    return member ? member.fullName || member.email : null;
  };

  const meQuery = trpc.auth.me.useQuery();
  const commentUsers: CommentUser[] = workspaceMembers.map((m) => ({
    id: m.id,
    fullName: m.fullName,
    email: m.email,
  }));
  const currentUser: CommentUser | undefined = meQuery.data
    ? commentUsers.find((u) => u.id === meQuery.data!.uid) || {
        id: meQuery.data.uid,
        fullName: 'Me',
        email: '',
      }
    : undefined;

  if (!task) return null;

  const featureBarItems: EntityFeatureBarItem<'details' | 'comments' | 'files' | 'notes'>[] = [
    {
      value: 'details',
      title: 'Description',
      description: 'Task details and description',
      icon: FileText,
    },
  ];

  if (showComments) {
    featureBarItems.push({
      value: 'comments',
      title: 'Comments',
      description:
        commentCount > 0
          ? `${commentCount} team comment${commentCount === 1 ? '' : 's'}`
          : 'Team discussion on this task',
      icon: MessageSquare,
      badge: commentCount > 0 ? commentCount : undefined,
    });
  }

  if (showFiles) {
    featureBarItems.push({
      value: 'files',
      title: 'Files',
      description:
        fileCount > 0
          ? `${fileCount} attached file${fileCount === 1 ? '' : 's'}`
          : 'Uploaded task attachments',
      icon: Paperclip,
      badge: fileCount > 0 ? fileCount : undefined,
    });
  }

  if (showNotes) {
    featureBarItems.push({
      value: 'notes',
      title: 'Notes',
      description: 'Private task scratchpad notes',
      icon: StickyNote,
    });
  }

  return (
    <div className="flex flex-col h-full bg-background min-h-0 overflow-hidden">
      {/* Header bar */}
      <div className="flex items-center justify-between px-6 py-3.5 border-b shrink-0">
        <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">
          Task Details
        </h3>
        <div className="flex items-center gap-2">
          {!isReadOnly && mode === 'view' && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2.5 text-xs gap-1.5"
              onClick={() => setMode('edit')}
            >
              <Edit3 className="h-3.5 w-3.5" />
              <span>Edit</span>
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Main Area */}
      {mode === 'view' ? (
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
          {/* Pinned Top Block: TaskCard + Feature Bar */}
          <div className="p-4 pb-3 shrink-0 space-y-3 border-b border-border/40 bg-muted/10">
            <TaskCard
              task={task as unknown as Task}
              statuses={statuses}
              members={workspaceMembers as WorkspaceMember[]}
              onClick={() => {}}
              onToggleComplete={!isReadOnly ? handleToggleDone : undefined}
              metaSlot={
                task.reminderFreq && task.reminderFreq !== 'never' ? (
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1 capitalize">
                    <Repeat className="w-3 h-3 text-muted-foreground" />
                    {task.reminderFreq}
                  </span>
                ) : null
              }
              className="cursor-default hover:shadow-xs border-border/70 p-3"
            />

            {task.reportedBy && task.reportedBy !== task.assignedTo && getReportedByName() && (
              <p className="text-[11px] text-muted-foreground italic px-1 -mt-1">
                Delegated by {getReportedByName()}
              </p>
            )}

            <EntityFeatureBar
              items={featureBarItems}
              activeTab={activeTab}
              onSelectTab={setActiveTab}
            />
          </div>

          {/* Active Tertiary Content Area */}
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {activeTab === 'details' && (
              <div className="flex-1 overflow-y-auto p-6">
                {task.content ? (
                  <div className="rounded-xl bg-slate-50/50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800/50 p-4 min-h-[100px]">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                      Description
                    </h4>
                    <RichTextContent
                      className="prose-sm max-w-none text-sm leading-relaxed text-slate-600 dark:text-slate-300"
                      html={ensureRichText(task.content)}
                    />
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-800 p-6 text-center">
                    <p className="text-sm text-muted-foreground italic">
                      {/* A description that was removed is not a description that was
                          never written. Saying so is the difference between a boundary
                          and a page that looks broken. */}
                      {task.contentRedacted
                        ? 'The description came from a conversation you do not have access to.'
                        : 'No additional description provided.'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'comments' && (
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                {currentUser ? (
                  <TaskCommentThread
                    taskId={task.id}
                    users={commentUsers}
                    currentUser={currentUser}
                    isReadOnly={isReadOnly}
                  />
                ) : null}
              </div>
            )}

            {activeTab === 'files' && (
              <div className="flex-1 min-h-0 overflow-y-auto p-6">
                <TaskFileAttachments taskId={task.id} isReadOnly={isReadOnly} />
              </div>
            )}

            {activeTab === 'notes' && (
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                <NotesPanel
                  entityType="task"
                  entityId={task.id}
                  isReadOnly={isReadOnly}
                  composerPosition="bottom"
                  placeholder="Capture a note about this task…"
                  className="h-full overflow-hidden p-4"
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6 scrollbar-thin">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit} className="space-y-6">
              <TaskFormFields
                statuses={statuses}
                members={workspaceMembers}
                disabled={isSubmitting}
              />

              <div className="pt-6 border-t flex flex-col gap-3">
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setMode('view')}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>

                {onDelete && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-2"
                    onClick={onDelete}
                    disabled={isDeleting}
                  >
                    <Trash className="h-3.5 w-3.5" />
                    {isDeleting ? 'Deleting...' : 'Delete Task'}
                  </Button>
                )}
              </div>
            </form>
          </FormProvider>
        </div>
      )}
    </div>
  );
}
